import { useRef, useState, type RefObject } from 'react';

interface WalkthroughProps {
  video: string;
  /** Narration recorded apart from the video; kept in sync with it. */
  audio: string | null;
  title: string;
  /** Video lessons: the parent seeks and plays through this element, and follows its time and end. */
  videoRef?: RefObject<HTMLVideoElement | null>;
  poster?: string | null;
  autoPlay?: boolean;
  onTime?: (seconds: number) => void;
  onEnded?: () => void;
}

/**
 * The lesson's recorded walkthrough: the browser's own video controls (play, seek, fullscreen), with the separate
 * narration track following the video. The source stays the one it started with, so a manifest refetch does not
 * restart it; if that link has expired by the next request, the newest one takes over at the same second.
 */
export function WalkthroughPlayer({ video, audio, title, videoRef: external, poster, autoPlay = false, onTime, onEnded }: WalkthroughProps) {
  const own = useRef<HTMLVideoElement>(null);
  const videoRef = external ?? own;
  const audioRef = useRef<HTMLAudioElement>(null);
  const [src, setSrc] = useState({ video, audio });

  const sync = (play: boolean) => {
    const v = videoRef.current;
    const a = audioRef.current;
    if (!v || !a) return;
    if (Math.abs(a.currentTime - v.currentTime) > 0.3) a.currentTime = v.currentTime;
    a.playbackRate = v.playbackRate;
    if (play) void a.play().catch(() => undefined);
    else a.pause();
  };
  const onError = () => {
    if (src.video === video) return;
    const at = videoRef.current?.currentTime ?? 0;
    setSrc({ video, audio });
    videoRef.current?.addEventListener('loadedmetadata', () => videoRef.current && (videoRef.current.currentTime = at), { once: true });
  };

  return (
    <div data-testid="lesson-walkthrough" className="overflow-hidden rounded-lg border bg-secondary shadow-card">
      <video
        ref={videoRef}
        src={src.video}
        controls
        playsInline
        preload="metadata"
        autoPlay={autoPlay}
        poster={poster ?? undefined}
        aria-label={`${title}: walkthrough video`}
        data-testid="lesson-video"
        className="aspect-video w-full bg-secondary"
        onPlay={() => sync(true)}
        onPause={() => sync(false)}
        onSeeked={() => sync(!videoRef.current?.paused)}
        onRateChange={() => sync(!videoRef.current?.paused)}
        onTimeUpdate={() => onTime?.(videoRef.current?.currentTime ?? 0)}
        onEnded={() => {
          audioRef.current?.pause();
          onEnded?.();
        }}
        onError={onError}
      />
      {src.audio ? <audio ref={audioRef} src={src.audio} preload="auto" hidden /> : null}
    </div>
  );
}
